import React, { forwardRef } from 'react';
import PropTypes from 'prop-types';
import { createMemoStyles, safeRest } from '@wds/utils';
import { useTheme } from 'react-jss';
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
function _objectWithoutProperties(source, excluded) {
  if (source == null) return {};
  var target = _objectWithoutPropertiesLoose(source, excluded);
  var key, i;
  if (Object.getOwnPropertySymbols) {
    var sourceSymbolKeys = Object.getOwnPropertySymbols(source);
    for (i = 0; i < sourceSymbolKeys.length; i++) {
      key = sourceSymbolKeys[i];
      if (excluded.indexOf(key) >= 0) continue;
      if (!Object.prototype.propertyIsEnumerable.call(source, key)) continue;
      target[key] = source[key];
    }
  }
  return target;
}
function _arrayLikeToArray(arr, len) {
  if (len == null || len > arr.length) len = arr.length;
  for (var i = 0, arr2 = new Array(len); i < len; i++) {
    arr2[i] = arr[i];
  }
  return arr2;
}
function _arrayWithoutHoles(arr) {
  if (Array.isArray(arr)) return _arrayLikeToArray(arr);
}
function _iterableToArray(iter) {
  if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _unsupportedIterableToArray(o, minLen) {
  if (!o) return;
  if (typeof o === "string") return _arrayLikeToArray(o, minLen);
  var n = Object.prototype.toString.call(o).slice(8, -1);
  if (n === "Object" && o.constructor) n = o.constructor.name;
  if (n === "Map" || n === "Set") return Array.from(o);
  if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _arrayLikeToArray(o, minLen);
}
function _nonIterableSpread() {
  throw new TypeError("Invalid attempt to spread non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _toConsumableArray(arr) {
  return _arrayWithoutHoles(arr) || _iterableToArray(arr) || _unsupportedIterableToArray(arr) || _nonIterableSpread();
}
var SEMANTIC_COLORS = Object.freeze(['primary', 'success', 'info', 'warning', 'upgrade', 'secondary', 'muted']);
var TYPE_COLORS = Object.freeze(['dark', 'light', 'darkMuted', 'lightMuted', 'link']);
var SIZES = Object.freeze(['lg', 'md', 'sm']);
Object.freeze(['primary', 'upgrade', 'info', 'success', 'warning', 'secondary']);
Object.freeze([].concat(_toConsumableArray(SIZES), ['xs']));
Object.freeze(['md', 'sm']);
Object.freeze(['success', 'warning']);
Object.freeze(['success', 'warning']);
Object.freeze(['data-one', 'data-two', 'data-three', 'data-four', 'data-five', 'data-six', 'data-seven', 'data-eight', 'data-nine', 'data-ten']);
Object.freeze({
  DOWN: 'ArrowDown',
  END: 'End',
  ESC: 'Escape',
  HOME: 'Home',
  LEFT: 'ArrowLeft',
  PAGE_DOWN: 'PageDown',
  PAGE_UP: 'PageUp',
  RIGHT: 'ArrowRight',
  UP: 'ArrowUp',
  ENTER: 'Enter',
  SPACE: ' ',
  BACKSPACE: 'Backspace',
  TAB: 'Tab'
}); // Deprecated. Moving to e.key

Object.freeze({
  DOWN: 40,
  END: 35,
  ESC: 27,
  HOME: 36,
  LEFT: 37,
  PAGE_DOWN: 34,
  PAGE_UP: 33,
  RIGHT: 39,
  UP: 38,
  ENTER: 13,
  SPACE: 32,
  BACKSPACE: 8,
  TAB: 9
});
Object.freeze({
  37: -1,
  38: -1,
  39: 1,
  40: 1
});
Object.freeze(['top', 'bottom', 'right', 'left']);
Object.freeze(['primary', 'secondary', 'muted', 'success', 'warning', 'upgrade', 'info']);
Object.freeze(['light', 'dark']);
Object.freeze(['primary', 'light', 'dark', 'transparent']);
Object.freeze(['left', 'right', 'even', 'center']);
Object.freeze(['normal', 'near', 'far']);
Object.freeze(['up', 'right', 'down', 'left']);
Object.freeze(['slow', 'normal', 'fast']);
Object.freeze({
  speed: {
    slow: 300,
    normal: 200,
    fast: 100
  }
});
Object.freeze({
  speed: {
    slow: 450,
    normal: 350,
    fast: 250
  },
  distance: {
    normal: 25,
    near: 5,
    far: 75
  }
});
Object.freeze({
  accordion: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A7&scaling=min-zoom&page-id=30%3A2',
  'action-sheet': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A293&scaling=min-zoom&page-id=30%3A288',
  alert: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A317&scaling=min-zoom&page-id=30%3A312',
  avatar: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A293&scaling=min-zoom&page-id=4%3A288',
  badge: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A1243&scaling=min-zoom&page-id=4%3A1238',
  banner: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A341&scaling=min-zoom&page-id=30%3A336',
  /**
   * TODO: Currently goes to Global Padding, change to box when created
   */
  box: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A942&scaling=min-zoom&page-id=30%3A937',
  button: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=20%3A9&scaling=min-zoom&page-id=20%3A4',
  'button-group': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A389&scaling=min-zoom&page-id=30%3A384',
  card: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A437&scaling=min-zoom&page-id=30%3A432',
  checkbox: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A1037&scaling=min-zoom&page-id=4%3A1032',
  coachmark: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A413&scaling=min-zoom&page-id=30%3A408',
  'form-group': '',
  grid: '',
  icons: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=895%3A13856&scaling=min-zoom&page-id=119%3A2',
  input: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A485&scaling=min-zoom&page-id=30%3A480',
  /**
   * TODO: This doesn't look like it's up to date right now
   */
  'input-group': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A509&scaling=min-zoom&page-id=30%3A504',
  link: '',
  list: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A893&scaling=min-zoom&page-id=30%3A888',
  menu: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A533&scaling=min-zoom&page-id=30%3A528',
  modal: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A557&scaling=min-zoom&page-id=30%3A552',
  motion: '',
  multiselect: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A581&scaling=min-zoom&page-id=30%3A576',
  'number-picker': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A629&scaling=min-zoom&page-id=30%3A624',
  'page-header': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A653&scaling=min-zoom&page-id=30%3A648',
  pagination: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A677&scaling=min-zoom&page-id=30%3A672',
  popout: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A701&scaling=min-zoom&page-id=30%3A696',
  'progress-bar': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A715&scaling=min-zoom&page-id=4%3A710',
  'progress-circle': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A715&scaling=min-zoom&page-id=4%3A710',
  radio: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A1037&scaling=min-zoom&page-id=4%3A1032',
  /**
   * TODO: Doesn't look complete yet, update if necessary
   */
  responsive: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=304%3A1897&scaling=min-zoom&page-id=160%3A17',
  select: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A725&scaling=min-zoom&page-id=30%3A720',
  'select-menu': 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A725&scaling=min-zoom&page-id=30%3A720',
  sheet: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A749&scaling=min-zoom&page-id=30%3A744',
  slider: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A773&scaling=min-zoom&page-id=30%3A768',
  switch: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=4%3A1037&scaling=min-zoom&page-id=4%3A1032',
  table: '',
  tabs: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A797&scaling=min-zoom&page-id=30%3A792',
  textarea: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A821&scaling=min-zoom&page-id=30%3A816',
  toast: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A845&scaling=min-zoom&page-id=30%3A840',
  tooltip: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=30%3A869&scaling=min-zoom&page-id=30%3A864',
  typography: 'https://www.figma.com/proto/P5WtS8mSjaKYnWVYKnSM67/Design-System-WDS-UI-Kit?node-id=18%3A8&scaling=min-zoom&page-id=18%3A3'
});
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}
function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    if (enumerableOnly) {
      symbols = symbols.filter(function (sym) {
        return Object.getOwnPropertyDescriptor(object, sym).enumerable;
      });
    }
    keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i] != null ? arguments[i] : {};
    if (i % 2) {
      ownKeys(Object(source), true).forEach(function (key) {
        _defineProperty(target, key, source[key]);
      });
    } else if (Object.getOwnPropertyDescriptors) {
      Object.defineProperties(target, Object.getOwnPropertyDescriptors(source));
    } else {
      ownKeys(Object(source)).forEach(function (key) {
        Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
      });
    }
  }
  return target;
}
var useStyles = createMemoStyles(function (theme) {
  return {
    wrenchTypography: function wrenchTypography(_ref) {
      var align = _ref.align,
        color = _ref.color,
        variant = _ref.variant;
      var _theme$type = theme.type,
        fontFamily = _theme$type.fontFamily,
        fontSize = _theme$type.fontSize,
        fontWeight = _theme$type.fontWeight,
        palette = theme.palette;
      var chosenColor = palette[color] && palette[color].main || palette.text[color];
      return _objectSpread(_objectSpread(_objectSpread({
        fontFamily: fontFamily.base,
        fontSize: fontSize.body,
        boxSizing: 'border-box',
        '-webkit-font-smoothing': 'antialiased',
        color: chosenColor,
        textAlign: align,
        lineHeight: 1.5,
        margin: 0,
        padding: 0
      }, variant && variant !== 'link' && {
        fontSize: fontSize[variant],
        fontWeight: {
          hero1: fontWeight.light,
          hero2: fontWeight.light,
          title: fontWeight.light,
          sectionTitle: fontWeight.light,
          pageTitle: fontWeight.light,
          cardTitle: fontWeight.medium,
          body: fontWeight.regular,
          bodySm: fontWeight.regular
        }[variant]
      }), variant && variant === 'link' && {
        color: palette.link.main,
        textDecoration: 'none',
        border: 'none',
        background: 'none',
        outline: 'none',
        cursor: 'pointer',
        fontWeight: 'inherit',
        fontSize: 'inherit',
        '&:hover, &:focus': {
          textDecoration: 'underline'
        }
      }), variant && (variant === 'hero1' || variant === 'hero2') && {
        fontFamily: fontFamily.headings
      });
    }
  };
});
var _excluded = ["align", "children", "color", "component", "variant"];
var ALIGNMENT = ['center', 'left', 'right'];
var VARIANTS = ['body', 'bodySm', 'cardTitle', 'hero1', 'hero2', 'link', 'pageTitle', 'sectionTitle']; // @todo: custom components wanted like <Link />??

var COMPONENT_LIST = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'a', 'p', 'span', 'div', 'article', 'section'];
var Typography = /*#__PURE__*/forwardRef(function (_ref, ref) {
  var align = _ref.align,
    children = _ref.children,
    color = _ref.color,
    component = _ref.component,
    variant = _ref.variant,
    rest = _objectWithoutProperties(_ref, _excluded);
  var Component = component;
  var theme = useTheme();
  var _useStyles = useStyles({
      align: align,
      color: color,
      variant: variant,
      theme: theme
    }),
    wrenchTypography = _useStyles.wrenchTypography;
  return /*#__PURE__*/React.createElement(Component, Object.assign({
    className: wrenchTypography
  }, safeRest(rest), {
    ref: ref
  }), children);
});
Typography.propTypes = {
  /**
   * Align content
   */
  align: PropTypes.oneOf(ALIGNMENT),
  /**
   * Text content to be displayed
   */
  children: PropTypes.node.isRequired,
  /**
   * Color variants
   */
  color: PropTypes.oneOf(SEMANTIC_COLORS.concat(TYPE_COLORS)),
  /**
   * Must be one of the following components:
   */
  component: PropTypes.oneOf(COMPONENT_LIST),
  /**
   * Our variants of choice are:
   */
  variant: PropTypes.oneOf(VARIANTS)
};
Typography.defaultProps = {
  align: 'left',
  component: 'div',
  color: 'dark',
  variant: 'body'
};
Typography.__docgenInfo = {
  "description": "",
  "methods": [],
  "displayName": "Typography",
  "props": {
    "align": {
      "defaultValue": {
        "value": "'left'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'center'",
          "computed": false
        }, {
          "value": "'left'",
          "computed": false
        }, {
          "value": "'right'",
          "computed": false
        }]
      },
      "required": false,
      "description": "Align content"
    },
    "component": {
      "defaultValue": {
        "value": "'div'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'h1'",
          "computed": false
        }, {
          "value": "'h2'",
          "computed": false
        }, {
          "value": "'h3'",
          "computed": false
        }, {
          "value": "'h4'",
          "computed": false
        }, {
          "value": "'h5'",
          "computed": false
        }, {
          "value": "'h6'",
          "computed": false
        }, {
          "value": "'a'",
          "computed": false
        }, {
          "value": "'p'",
          "computed": false
        }, {
          "value": "'span'",
          "computed": false
        }, {
          "value": "'div'",
          "computed": false
        }, {
          "value": "'article'",
          "computed": false
        }, {
          "value": "'section'",
          "computed": false
        }]
      },
      "required": false,
      "description": "Must be one of the following components:"
    },
    "color": {
      "defaultValue": {
        "value": "'dark'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "computed": true,
        "value": "SEMANTIC_COLORS.concat(TYPE_COLORS)"
      },
      "required": false,
      "description": "Color variants"
    },
    "variant": {
      "defaultValue": {
        "value": "'body'",
        "computed": false
      },
      "type": {
        "name": "enum",
        "value": [{
          "value": "'body'",
          "computed": false
        }, {
          "value": "'bodySm'",
          "computed": false
        }, {
          "value": "'cardTitle'",
          "computed": false
        }, {
          "value": "'hero1'",
          "computed": false
        }, {
          "value": "'hero2'",
          "computed": false
        }, {
          "value": "'link'",
          "computed": false
        }, {
          "value": "'pageTitle'",
          "computed": false
        }, {
          "value": "'sectionTitle'",
          "computed": false
        }]
      },
      "required": false,
      "description": "Our variants of choice are:"
    },
    "children": {
      "type": {
        "name": "node"
      },
      "required": true,
      "description": "Text content to be displayed"
    }
  }
};
export { Typography };